'use client'

import React, { useState, useEffect } from 'react'
import { Image, useTranslations, useLocale } from '@/lib/next-shim'
import { SubmitHandler } from 'react-hook-form'
import { useRouter, useSearchParams } from '@/lib/next-shim'
import { useSession } from '@/shared/ui/providers/AuthSessionProvider'

import { Alert, Box, Button, Container, Paper, Stack, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import logoGif from '@/assets/img/logo.gif'
import { useSignUpMutation } from '@/core/graphql/hooks'
import { shouldUseCustomBranding } from '@/utils/landing-variant'
import { getEnv } from '@/lib/env'

import { SocialLoginButtons } from '@/shared/ui/buttons/SocialLoginButtons'
import { RegisterSuccess } from './RegisterSuccess'
import { DefaultRegisterForm } from './DefaultRegisterForm'
import { DefaultRegisterFormData } from '../model/defaultRegisterForm'

export function DefaultRegisterPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const callbackUrl = searchParams?.get('callbackUrl')
  const isFromBooking = callbackUrl?.includes('meeting-links') || false
  const { status } = useSession()
  const t = useTranslations()

  const isCustomBranded = shouldUseCustomBranding()
  const brandLogo = getEnv('NEXT_PUBLIC_BRAND_LOGO', '')
  const brandName = getEnv('NEXT_PUBLIC_BRAND_NAME', 'Medconnect Platform')

  useEffect(() => {
    if (status === 'authenticated' && callbackUrl) {
      router.replace(callbackUrl)
    }
  }, [status, router, callbackUrl])

  const [signUpSuccess, setSignUpSuccess] = useState(false)
  const [registrationError, setRegistrationError] = useState<string | null>(null)

  const [signUp, { loading, error }] = useSignUpMutation()
  const locale = useLocale()

  const onSubmit: SubmitHandler<DefaultRegisterFormData> = (data) => {
    const { terms, confirmPassword, password, ...payload } = data
    signUp({
      variables: { payload: { ...payload, password, language: locale, callbackUrl: callbackUrl || undefined } },
      onCompleted: async (data) => {
        setSignUpSuccess(true)
        window.location.href = data.signUp
      },
      onError: (error) => {
        setRegistrationError(error.message || 'Registration failed. Please try again.')
      },
    })
  }

  const handleLoginClick = () => {
    if (callbackUrl) {
      router.push(`/login?callbackUrl=${encodeURIComponent(callbackUrl)}`)
    } else {
      router.push('/login')
    }
  }

  if (signUpSuccess) {
    return <RegisterSuccess />
  }

  return (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      {isFromBooking && (
        <Alert
          icon={<InfoOutlinedIcon fontSize="inherit" />}
          severity="info"
          variant="outlined"
          sx={{
            // Stick the banner right below the fixed auth AppBar (h-[60px]) while scrolling.
            position: 'sticky',
            top: '60px',
            zIndex: (theme) => theme.zIndex.appBar - 1,
            bgcolor: 'background.paper',
            borderRadius: 0,
            borderTop: 0,
            borderLeft: 0,
            borderRight: 0,
            alignItems: { xs: 'flex-start', sm: 'center' },
            '& .MuiAlert-message': { flex: 1 },
          }}
          action={
            <Button onClick={() => router.back()} size="small" variant="outlined">
              {t('button.back.label')}
            </Button>
          }
        >
          <Typography sx={{ display: { xs: 'block', sm: 'none' } }}>{t('page.Register.bookingContext.short')}</Typography>
          <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>{t('page.Register.bookingContext.full')}</Typography>
        </Alert>
      )}

      <Container maxWidth="lg" sx={{ flex: 1, py: 5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={4} alignItems={{ xs: 'stretch', lg: 'flex-start' }} justifyContent="center">
          <Paper
            elevation={2}
            sx={{
              width: '100%',
              maxWidth: 500,
              borderRadius: 2,
              overflow: 'hidden',
              mx: 'auto',
            }}
          >
            <Stack spacing={3} sx={{ p: { xs: 3, sm: 4 } }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
                  {isCustomBranded ? t('page.Register.signUpToBrand', { brand: brandName }) : t('page.Register.title')}
                </Typography>
                <Button onClick={handleLoginClick} variant="outlined" type="button">
                  {t('button.signIn.label')}
                </Button>
              </Stack>

              {(error || registrationError) && (
                <Alert severity="error" role="alert">
                  {error?.message || registrationError}
                </Alert>
              )}

              <SocialLoginButtons callbackUrl={callbackUrl || undefined} />

              <DefaultRegisterForm onSubmit={onSubmit} isLoading={loading} />
            </Stack>
          </Paper>

          <Box
            sx={{
              display: { xs: 'none', lg: 'flex' },
              width: 500,
              height: 500,
              alignItems: 'flex-start',
              justifyContent: 'center',
              p: 4,
              pt: 8,
            }}
          >
            <Stack alignItems="center" spacing={2}>
              {isCustomBranded ? (
                <>
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                    {brandName}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('page.Register.tagline')}
                  </Typography>
                </>
              ) : (
                t.rich('page.Register.banner', {
                  title: (chunks: React.ReactNode) => (
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                      {chunks}
                    </Typography>
                  ),
                  subtitle: (chunks: React.ReactNode) => (
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
                      {chunks}
                    </Typography>
                  ),
                })
              )}
              {isCustomBranded && brandLogo ? (
                <Image alt={brandName} src={brandLogo} priority width={178} height={100} style={{ height: 96, width: 'auto', objectFit: 'contain' }} />
              ) : (
                <Image alt="logo" src={logoGif} priority width={178} height={100} style={{ height: 96 }} />
              )}
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  )
}
